var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { action, thunk } from 'easy-peasy';
import i18n from '../helper/i18n';
import { buildPagesData, LocalDataSource, IdmDataSource, IdmShareDataSource, ShareCodeDataSource, fetchShareCodeData, applyTranslationOverrides, mergePageData, extractUserSaveState, } from '../middleware/jsonMiddleware';
import customEventHandler from '../helper/customEvents';
import { setAudienceContext, applyMatomoOptOut } from '../helper/analytics';
import { API_BASE_URL } from '../config/api';
import { formatShareCode } from '../helper/shareCode';
import { isLocalLoginMode, DUMMY_USER, isDummyOwnedCode } from '../helper/localLoginMode';
import { parseMasquerade } from '../helper/masquerade';
import { isRestrictedContent, isShareCodeOwner } from '../helper/shareRestriction';
import { applyDraftToUserState, draftActivityKey, readDraft, } from '../helper/unsavedDraft';
// Store-agnostic PDF utilities — importing directly from pdfCache avoids
// the circular dependency that would arise from importing BackgroundRenderer
// (which imports globalStore → playerStore → BackgroundRenderer).
import { preloadPdfBackground, getPdfPageDimensions } from '../helper/pdfCache';
// Touch/tablet devices get a smaller canvas to prevent GPU memory crashes
var isTouch = typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches;
var defaultCanvasSize = isTouch ? 3000 : 3000;
var playerStore = {
    displayMode: 'normal',
    gameLoaded: false,
    contrast: 'normal',
    zoomLevel: 1,
    canvasHeight: defaultCanvasSize,
    canvasWidth: defaultCanvasSize,
    // Physical canvas is smaller for performance, CSS scales it up
    physicalCanvasScale: 1, // 5000 * 0.5 = 2500 physical pixels
    language: 'en',
    currentPage: 1,
    totalPages: 10,
    isAdmin: true,
    viewEditMode: 'edit',
    playerIconZoom: 1,
    playerBgPattern: 'none',
    isIDM: false,
    isSwp: false,
    isLoggedIn: true,
    username: null,
    userId: null,
    shareCode: null,
    shareCodeMissing: null,
    // Defaults to true: only an explicit `shareable: false` restricts anything,
    // so ordinary activities and pre-existing share codes are unaffected.
    contentShareable: true,
    shareCodeRestricted: false,
    masquerade: null,
    pageData: [],
    productionBaseline: null,
    activityMeta: null,
    pdfBackgroundLoading: false,
    cachedRawData: null,
    nmpAnimationEnabled: true,
    isFullScreen: false,
    manipulativeBtnState: null,
    setPdfBackgroundLoading: action(function (state, payload) {
        state.pdfBackgroundLoading = payload;
    }),
    setStoredManipulativeBtnState: action(function (state, payload) {
        state.manipulativeBtnState = payload !== null && payload !== void 0 ? payload : null;
    }),
    setNmpAnimationEnabled: action(function (state, payload) {
        state.nmpAnimationEnabled = payload;
    }),
    setIsFullScreen: action(function (state, payload) {
        state.isFullScreen = payload;
    }),
    setDisplayMode: action(function (state, payload) {
        state.displayMode = payload;
    }),
    setGameLoaded: action(function (state, payload) {
        state.gameLoaded = payload;
    }),
    setContrast: action(function (state, payload) {
        state.contrast = payload;
    }),
    setZoomLevel: action(function (state, payload) {
        state.zoomLevel = payload;
    }),
    // User-initiated zoom (zoom buttons, keyboard shortcut, zoom-to-fit). Unlike
    // the bare setZoomLevel — which the automatic fit-zoom effects use and which
    // must NOT persist — this also stamps the zoom onto the current page so it is
    // saved (extractUserSaveState.userZoom) and restored on reload. Auto-fit pages
    // keep PageData.zoomLevel undefined and re-fit responsively next time.
    setUserZoom: thunk(function (actions, payload, _a) {
        var getState = _a.getState;
        actions.setZoomLevel(payload);
        var _b = getState(), currentPage = _b.currentPage, pageData = _b.pageData;
        // Only persist the zoom level when the page slot already exists in
        // pageData. If pageData is empty (no IDM/share-code activity loaded),
        // writing to slot 0 would change pageData.length from 0 → 1, which
        // triggers DataContext's [pageData.length] re-initialization effect and
        // wipes any manipulatives the user placed on the blank canvas.
        if (!pageData[currentPage - 1])
            return;
        actions.updatePageData({
            pageIndex: currentPage - 1,
            data: { zoomLevel: payload },
        });
    }),
    // Records the selection whose right-side tool panel is open, so reopening the
    // activity restores it. Stamped onto the current page (persisted via
    // extractUserSaveState.openPanelSelection) AND mirrored to localStorage by the
    // caller, because a browser refresh never goes through a save.
    //
    // Carries the same guard as setUserZoom, for the same reason: if the page slot
    // does not exist yet, writing it would take pageData.length 0 -> 1 and trip
    // DataContext's [pageData.length] re-initialisation effect, wiping any
    // manipulatives the user placed on a blank canvas.
    setOpenPanelSelection: thunk(function (actions, payload, _a) {
        var getState = _a.getState;
        var _b = getState(), currentPage = _b.currentPage, pageData = _b.pageData;
        if (!pageData[currentPage - 1])
            return;
        if (pageData[currentPage - 1].openPanelSelection === (payload !== null && payload !== void 0 ? payload : undefined)) {
            return;
        }
        actions.updatePageData({
            pageIndex: currentPage - 1,
            data: { openPanelSelection: payload !== null && payload !== void 0 ? payload : undefined },
        });
    }),
    setCanvasDimension: action(function (state, payload) {
        state.canvasHeight = payload.height;
        state.canvasWidth = payload.width;
    }),
    setIsAdmin: action(function (state, payload) {
        state.isAdmin = payload;
    }),
    setViewEditMode: action(function (state, payload) {
        state.viewEditMode = payload;
    }),
    setLanguage: action(function (state, payload) {
        state.language = payload;
        i18n.changeLanguage(payload);
        document.documentElement.lang = payload;
    }),
    setCachedRawData: action(function (state, payload) {
        state.cachedRawData = payload;
    }),
    // Switch the activity's locale and re-merge pageData in place so dice,
    // cards, and counter images update without a re-fetch. Drawings, text,
    // annotations, shades, and tables already on the page are preserved via
    // `extractUserSaveState` — the merge sees them through the synthesised
    // user workspace, just like a reload from a saved state would.
    applyLanguage: thunk(function (actions_1, language_1, _a) { return __awaiter(void 0, [actions_1, language_1, _a], void 0, function (actions, language, _b) {
        var state, _c, cachedRawData, productionBaseline, canvasWidth, canvasHeight, shareCode, currentPage, isIDM, isSwp, liveState, userSaveState, localizedInitial, safeProduction, pageIds, merged, newCurrent;
        var _d, _e, _f;
        var getState = _b.getState;
        return __generator(this, function (_g) {
            state = getState();
            if (state.language === language)
                return [2 /*return*/];
            // Always update the language state first so any UI listening on `language`
            // (i18n, resolvePageBackground, etc.) re-renders immediately.
            actions.setLanguage(language);
            _c = getState(), cachedRawData = _c.cachedRawData, productionBaseline = _c.productionBaseline, canvasWidth = _c.canvasWidth, canvasHeight = _c.canvasHeight, shareCode = _c.shareCode, currentPage = _c.currentPage, isIDM = _c.isIDM, isSwp = _c.isSwp;
            if (!cachedRawData)
                return [2 /*return*/];
            // Ask DataContext to flush the live page (current page widgets, text,
            // drawings, etc. live in DataContext state, not in pageData until the
            // user navigates pages). The listener calls updatePageData synchronously,
            // so the next getState() reads the up-to-date pageData.
            customEventHandler.dispatchEvent('flushPageDataForRelocalize');
            liveState = getState();
            userSaveState = extractUserSaveState(liveState.pageData, productionBaseline, { username: (_d = liveState.username) !== null && _d !== void 0 ? _d : '', activityUUID: (_f = (_e = cachedRawData.initial) === null || _e === void 0 ? void 0 : _e.activityId) !== null && _f !== void 0 ? _f : '' });
            localizedInitial = applyTranslationOverrides(cachedRawData.initial, language);
            safeProduction = productionBaseline !== null && productionBaseline !== void 0 ? productionBaseline : cachedRawData.production;
            pageIds = Object.keys(localizedInitial.pages);
            merged = pageIds.map(function (pageId, index) {
                return mergePageData(localizedInitial, safeProduction, userSaveState, pageId, index, { canvasWidth: canvasWidth, canvasHeight: canvasHeight }, !!shareCode, 
                // Preserve the deck size computed at the initial load — switching
                // language must not resize cards, portrait or landscape.
                true, isIDM || isSwp);
            });
            merged.forEach(function (page, index) {
                actions.updatePageData({ pageIndex: index, data: page });
            });
            // Refresh translated tray-counter art too.
            if (liveState.activityMeta) {
                actions.setActivityMeta(__assign(__assign({}, liveState.activityMeta), { trayCounters: localizedInitial.trayCounters }));
            }
            newCurrent = merged[currentPage - 1];
            if (newCurrent) {
                customEventHandler.dispatchEvent('relocalizeApplied', { page: newCurrent });
            }
            return [2 /*return*/];
        });
    }); }),
    setPages: action(function (state, payload) {
        state.currentPage = payload.currentPage;
        state.totalPages = payload.totalPages;
    }),
    setplayerIconZoom: action(function (state, payload) {
        state.playerIconZoom = payload;
    }),
    setPlayerBgPattern: action(function (state, payload) {
        state.playerBgPattern = payload;
    }),
    setIsIDM: action(function (state, payload) {
        state.isIDM = payload;
    }),
    setIsSwp: action(function (state, payload) {
        state.isSwp = payload;
    }),
    setIsLoggedIn: action(function (state, payload) {
        state.isLoggedIn = payload;
    }),
    setUsername: action(function (state, payload) {
        state.username = payload;
    }),
    setUserId: action(function (state, payload) {
        state.userId = payload;
    }),
    setShareCode: action(function (state, payload) {
        state.shareCode = payload;
    }),
    setShareCodeMissing: action(function (state, payload) {
        state.shareCodeMissing = payload;
    }),
    setContentShareable: action(function (state, payload) {
        state.contentShareable = payload;
    }),
    setShareCodeRestricted: action(function (state, payload) {
        state.shareCodeRestricted = payload;
    }),
    setMasquerade: action(function (state, payload) {
        state.masquerade = payload;
    }),
    updatePageData: action(function (state, payload) {
        var _a;
        // Merge so callers can pass partial updates (e.g. only widgets/text) without
        // dropping the page's `background`, `pageId`, or `bgOffset` metadata.
        var existing = (_a = state.pageData[payload.pageIndex]) !== null && _a !== void 0 ? _a : {};
        state.pageData[payload.pageIndex] = __assign(__assign({}, existing), payload.data);
    }),
    setProductionBaseline: action(function (state, payload) {
        state.productionBaseline = payload;
    }),
    setActivityMeta: action(function (state, payload) {
        state.activityMeta = payload;
    }),
    // Gate for a share code that holds non-shareable MLC content: resolves the
    // viewer, then answers "must this code be refused?".
    //
    // It lives in the store rather than in PlayerMainContainer because the answer
    // depends on `userId`/`username`, which only exist once checkAuth has
    // resolved. A caller in a component would have to read those out of a closure
    // captured BEFORE the await, and would see the signed-out defaults — silently
    // refusing the real owner their own activity.
    //
    // Returns true when the caller must abandon the load.
    checkShareCodeAccess: thunk(function (actions_1, payload_1, _a) { return __awaiter(void 0, [actions_1, payload_1, _a], void 0, function (actions, payload, _b) {
        var shareData, restrictedOverride, savedShareable, restricted, _c, userId, username, refused;
        var _d, _e;
        var getState = _b.getState;
        return __generator(this, function (_f) {
            switch (_f.label) {
                case 0:
                    shareData = payload.shareData, restrictedOverride = payload.restrictedOverride;
                    savedShareable = (_d = shareData === null || shareData === void 0 ? void 0 : shareData.meta) === null || _d === void 0 ? void 0 : _d.shareable;
                    restricted = restrictedOverride === true || isRestrictedContent(savedShareable);
                    // Cheap exit for the overwhelmingly common case — an ordinary activity, or
                    // a code saved before the flag existed. No auth round-trip is forced on it.
                    if (!restricted)
                        return [2 /*return*/, false];
                    return [4 /*yield*/, actions.checkAuth()];
                case 1:
                    _f.sent();
                    _c = getState(), userId = _c.userId, username = _c.username;
                    refused = !isShareCodeOwner({
                        savedShareable: savedShareable,
                        createdBy: shareData === null || shareData === void 0 ? void 0 : shareData.createdBy,
                        ownerUsername: (_e = shareData === null || shareData === void 0 ? void 0 : shareData.meta) === null || _e === void 0 ? void 0 : _e.username,
                        viewerId: userId,
                        viewerUsername: username,
                    });
                    if (refused)
                        actions.setShareCodeRestricted(true);
                    return [2 /*return*/, refused];
            }
        });
    }); }),
    checkAuth: thunk(function (actions_1, _payload_1, _a) { return __awaiter(void 0, [actions_1, _payload_1, _a], void 0, function (actions, _payload, _b) {
        var _c, shareCode, isIDM, isSwp, contentType, response, body, meUser, username, email, rawUserId, userId, shareCode, data, ownerId, ownsById, ownerName, myName, ownsByName, _d, shareCode, isIDM, isSwp, userId, contentType, _e, _f, shareCode, isIDM, isSwp, contentType;
        var _g, _h;
        var getState = _b.getState;
        return __generator(this, function (_j) {
            switch (_j.label) {
                case 0:
                    // Local dev "logged-in" mode (`?local-login` on localhost): there is no
                    // backend to authenticate against, so fake a logged-in session with a dummy
                    // user and skip the /users/me fetch. No-op off localhost — see
                    // src/helper/localLoginMode.ts.
                    if (isLocalLoginMode()) {
                        actions.setIsLoggedIn(true);
                        actions.setUsername(DUMMY_USER.username);
                        actions.setUserId(DUMMY_USER.userId);
                        // No backend to masquerade through in local-login mode.
                        actions.setMasquerade(null);
                        _c = getState(), shareCode = _c.shareCode, isIDM = _c.isIDM, isSwp = _c.isSwp;
                        // Ownership from the dummy saved-work list, mirroring the real branch
                        // below: a code you "own" (or no code at all) gives isAdmin = true, any
                        // other code makes you a logged-in NON-owner. This used to be a flat
                        // `true`, which made every code look like your own and left the whole
                        // non-owner half of the UI unreachable offline — the view/edit seed, the
                        // save-edits reminder and Save As. Load any code that isn't in the dummy
                        // list (e.g. `?FORE-IGN1&local-login`) to exercise those.
                        actions.setIsAdmin(isDummyOwnedCode(shareCode));
                        contentType = isIDM ? 'IDM' : isSwp ? 'SWP' : shareCode ? 'shared' : 'free';
                        setAudienceContext({ isLoggedIn: true, contentType: contentType, userId: DUMMY_USER.userId });
                        return [2 /*return*/, true];
                    }
                    _j.label = 1;
                case 1:
                    _j.trys.push([1, 11, , 13]);
                    return [4 /*yield*/, fetch("".concat(API_BASE_URL, "users/me"), {
                            credentials: 'include',
                        })];
                case 2:
                    response = _j.sent();
                    actions.setIsLoggedIn(response.ok);
                    if (!response.ok) return [3 /*break*/, 8];
                    return [4 /*yield*/, response.json().catch(function () { return null; })];
                case 3:
                    body = _j.sent();
                    meUser = body === null || body === void 0 ? void 0 : body.user;
                    username = meUser === null || meUser === void 0 ? void 0 : meUser.username;
                    email = meUser === null || meUser === void 0 ? void 0 : meUser.email;
                    if (username)
                        actions.setUsername(username);
                    // An admin impersonating another account: /me describes the MASQUERADED
                    // user (so `username`/`userId` above are theirs, not the admin's) and
                    // names the admin under `user.masquerade`. Set unconditionally, not only
                    // when present, so ending a masquerade and re-checking clears it.
                    actions.setMasquerade(parseMasquerade(meUser));
                    // Exclude opted-out users (by email; username as a fallback in case
                    // usernames are emails) from Matomo before any audience context /
                    // events are pushed below. Awaited so `optedOut` is set first.
                    return [4 /*yield*/, applyMatomoOptOut(email, username)];
                case 4:
                    // Exclude opted-out users (by email; username as a fallback in case
                    // usernames are emails) from Matomo before any audience context /
                    // events are pushed below. Awaited so `optedOut` is set first.
                    _j.sent();
                    rawUserId = meUser === null || meUser === void 0 ? void 0 : meUser.id;
                    userId = typeof rawUserId === 'number'
                        ? rawUserId
                        : typeof rawUserId === 'string' && rawUserId.trim() !== '' && !isNaN(Number(rawUserId))
                            ? Number(rawUserId)
                            : undefined;
                    actions.setUserId(typeof userId === 'number' ? userId : null);
                    shareCode = getState().shareCode;
                    if (!!shareCode) return [3 /*break*/, 5];
                    // No share code in the URL — the user is in their own workspace.
                    actions.setIsAdmin(true);
                    return [3 /*break*/, 7];
                case 5: return [4 /*yield*/, fetchShareCodeData(formatShareCode(shareCode))];
                case 6:
                    data = _j.sent();
                    ownerId = data === null || data === void 0 ? void 0 : data.createdBy;
                    ownsById = typeof userId === 'number' && ownerId === userId;
                    ownerName = (_h = (_g = data === null || data === void 0 ? void 0 : data.meta) === null || _g === void 0 ? void 0 : _g.username) === null || _h === void 0 ? void 0 : _h.trim().toLowerCase();
                    myName = username === null || username === void 0 ? void 0 : username.trim().toLowerCase();
                    ownsByName = !!ownerName && !!myName && ownerName === myName;
                    actions.setIsAdmin(ownsById || ownsByName);
                    _j.label = 7;
                case 7: return [3 /*break*/, 10];
                case 8:
                    actions.setUserId(null);
                    actions.setIsAdmin(true);
                    actions.setMasquerade(null);
                    // Guest: ensure any opt-out left by a previously-excluded user on this
                    // (possibly shared) browser is cleared so guests are tracked normally.
                    return [4 /*yield*/, applyMatomoOptOut()];
                case 9:
                    // Guest: ensure any opt-out left by a previously-excluded user on this
                    // (possibly shared) browser is cleared so guests are tracked normally.
                    _j.sent();
                    _j.label = 10;
                case 10:
                    // Tag the Matomo visit with audience dimensions (logged-in vs guest,
                    // content type, MLC vs embedded) now that auth + content flags are known.
                    {
                        _d = getState(), shareCode = _d.shareCode, isIDM = _d.isIDM, isSwp = _d.isSwp, userId = _d.userId;
                        contentType = isIDM ? 'IDM' : isSwp ? 'SWP' : shareCode ? 'shared' : 'free';
                        setAudienceContext({ isLoggedIn: response.ok, contentType: contentType, userId: userId });
                    }
                    return [2 /*return*/, response.ok];
                case 11:
                    _e = _j.sent();
                    actions.setIsLoggedIn(false);
                    actions.setUserId(null);
                    actions.setIsAdmin(true);
                    actions.setMasquerade(null);
                    // Unknown user on error → treat as not excluded and clear any stale opt-out.
                    return [4 /*yield*/, applyMatomoOptOut()];
                case 12:
                    // Unknown user on error → treat as not excluded and clear any stale opt-out.
                    _j.sent();
                    _f = getState(), shareCode = _f.shareCode, isIDM = _f.isIDM, isSwp = _f.isSwp;
                    contentType = isIDM ? 'IDM' : isSwp ? 'SWP' : shareCode ? 'shared' : 'free';
                    setAudienceContext({ isLoggedIn: false, contentType: contentType, userId: null });
                    return [2 /*return*/, false];
                case 13: return [2 /*return*/];
            }
        });
    }); }),
    loadActivityData: thunk(function (actions_1, source_1, _a) { return __awaiter(void 0, [actions_1, source_1, _a], void 0, function (actions, source, _b) {
        var _c, isIDM, isSwp, shareCode, revisionId_1, _d, canvasWidth_1, canvasHeight_1, dataSource, rawData_1, activityKey, draft, cachedSource, maxW, maxH, workspaces, _i, workspaces_1, ws, pdfUrls, seenPdfs, _e, _f, page, bg, _g, pdfUrls_1, url, dims, currentLanguage, _h, pagesData, productionBaseline, activityMeta, manipulativeBtnState, playerBgPattern, language, err_1, message;
        var _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u;
        var getState = _b.getState;
        return __generator(this, function (_v) {
            switch (_v.label) {
                case 0:
                    _c = getState(), isIDM = _c.isIDM, isSwp = _c.isSwp, shareCode = _c.shareCode;
                    if (!isIDM && !isSwp && !shareCode)
                        return [2 /*return*/, undefined];
                    _v.label = 1;
                case 1:
                    _v.trys.push([1, 6, , 7]);
                    revisionId_1 = (_j = window.__IDM_MAPPING__) === null || _j === void 0 ? void 0 : _j.revision_id;
                    _d = getState(), canvasWidth_1 = _d.canvasWidth, canvasHeight_1 = _d.canvasHeight;
                    dataSource = source !== null && source !== void 0 ? source : (function () {
                        if ((isIDM || isSwp) && shareCode && revisionId_1)
                            return new IdmShareDataSource(revisionId_1, shareCode, { canvasWidth: canvasWidth_1, canvasHeight: canvasHeight_1 });
                        if ((isIDM || isSwp) && revisionId_1)
                            return new IdmDataSource(revisionId_1);
                        if (shareCode)
                            return new ShareCodeDataSource(shareCode, { canvasWidth: canvasWidth_1, canvasHeight: canvasHeight_1 });
                        return new LocalDataSource();
                    })();
                    return [4 /*yield*/, dataSource.fetchAll()];
                case 2:
                    rawData_1 = _v.sent();
                    // A share-code-only load whose code resolves to nothing falls back to a
                    // blank workspace. Surface that so the user is told, rather than being
                    // handed an empty canvas with no explanation.
                    //
                    // Both share-code sources report this now, but they fall back to
                    // different things and the notice has to say which:
                    //   ShareCodeDataSource  (bare `/?CODE`)  -> blank workspace
                    //   IdmShareDataSource   (`/display-materials/<slug>?CODE`)
                    //                                          -> the activity's own baseline
                    //
                    // The IdmShareDataSource case was previously left unreported precisely
                    // because it is "not a blank workspace" — but silence is worse than a
                    // slightly different message. A teacher shared a work place through an
                    // LMS and her students silently got the activity's default setup; nothing
                    // in the app said the shared state had been dropped.
                    if ((dataSource instanceof ShareCodeDataSource ||
                        dataSource instanceof IdmShareDataSource) &&
                        dataSource.codeMissing) {
                        actions.setShareCodeMissing({
                            fallback: dataSource instanceof IdmShareDataSource ? 'activityDefault' : 'blank',
                            // A failure with no recorded reason means the backend was never the
                            // thing that failed (e.g. the dev json_files/ path). Treat it as
                            // 'notFound' — the historical behaviour — rather than inventing one.
                            reason: (_k = dataSource.codeMissingReason) !== null && _k !== void 0 ? _k : 'notFound',
                        });
                    }
                    // ── Unsaved-work draft (IDM/SWP with no share code).
                    //
                    // These sessions have nowhere to save to until the user deliberately
                    // creates a share code, so a refresh used to lose everything on the
                    // canvas. Any draft this browser stored for the activity is layered over
                    // the data source's `user` state here, which is the whole restore: the
                    // draft IS a save-state payload, so the existing merge does the rest.
                    //
                    // Gated on `!shareCode` on purpose — a share-code load is authoritative
                    // (it is the state the URL asked for, and possibly someone else's work),
                    // and silently mixing a local draft into it would corrupt what the
                    // recipient of a shared code sees.
                    if ((isIDM || isSwp) && !shareCode) {
                        activityKey = draftActivityKey();
                        draft = activityKey ? readDraft(activityKey) : null;
                        if (draft)
                            rawData_1.user = applyDraftToUserState(rawData_1.user, draft);
                    }
                    cachedSource = { fetchAll: function () { return Promise.resolve(rawData_1); } };
                    // Persist the raw payload so applyLanguage can re-run translation
                    // overrides and re-merge pageData without re-fetching from the network.
                    actions.setCachedRawData({
                        initial: rawData_1.initial,
                        production: rawData_1.production,
                        user: rawData_1.user,
                    });
                    maxW = canvasWidth_1;
                    maxH = canvasHeight_1;
                    workspaces = (_o = (_m = (_l = rawData_1.production) === null || _l === void 0 ? void 0 : _l.save_state) === null || _m === void 0 ? void 0 : _m.workspaces) !== null && _o !== void 0 ? _o : [];
                    for (_i = 0, workspaces_1 = workspaces; _i < workspaces_1.length; _i++) {
                        ws = workspaces_1[_i];
                        if (((_q = (_p = ws.dimensions) === null || _p === void 0 ? void 0 : _p.width) !== null && _q !== void 0 ? _q : 0) > maxW)
                            maxW = ws.dimensions.width;
                        if (((_s = (_r = ws.dimensions) === null || _r === void 0 ? void 0 : _r.height) !== null && _s !== void 0 ? _s : 0) > maxH)
                            maxH = ws.dimensions.height;
                    }
                    pdfUrls = [];
                    seenPdfs = new Set();
                    for (_e = 0, _f = Object.values((_u = (_t = rawData_1.initial) === null || _t === void 0 ? void 0 : _t.pages) !== null && _u !== void 0 ? _u : {}); _e < _f.length; _e++) {
                        page = _f[_e];
                        bg = page.background;
                        if (bg && /\.pdf(\?|$)/i.test(bg) && !seenPdfs.has(bg)) {
                            seenPdfs.add(bg);
                            pdfUrls.push(bg);
                        }
                    }
                    if (!(pdfUrls.length > 0)) return [3 /*break*/, 4];
                    // Preload in parallel; failures are silently ignored — a missing PDF
                    // simply won't influence the canvas size calculation.
                    return [4 /*yield*/, Promise.all(pdfUrls.map(function (url) { return preloadPdfBackground(url).catch(function () { return null; }); }))];
                case 3:
                    // Preload in parallel; failures are silently ignored — a missing PDF
                    // simply won't influence the canvas size calculation.
                    _v.sent();
                    for (_g = 0, pdfUrls_1 = pdfUrls; _g < pdfUrls_1.length; _g++) {
                        url = pdfUrls_1[_g];
                        dims = getPdfPageDimensions(url);
                        if (!dims)
                            continue;
                        if (dims.width > maxW)
                            maxW = dims.width;
                        if (dims.height > maxH)
                            maxH = dims.height;
                    }
                    _v.label = 4;
                case 4:
                    // ── Step 3: expand the canvas if any page content is larger than the
                    //    default canvas size.  We update the store so every downstream
                    //    consumer (WhiteBoard, SelectionLayer, clamping logic …) already sees
                    //    the correct dimensions when buildPagesData runs.
                    if (maxW > canvasWidth_1 || maxH > canvasHeight_1) {
                        canvasWidth_1 = maxW;
                        canvasHeight_1 = maxH;
                        actions.setCanvasDimension({ width: canvasWidth_1, height: canvasHeight_1 });
                    }
                    currentLanguage = getState().language;
                    return [4 /*yield*/, buildPagesData(cachedSource, { canvasWidth: canvasWidth_1, canvasHeight: canvasHeight_1 }, !!shareCode, currentLanguage, isIDM || isSwp)];
                case 5:
                    _h = _v.sent(), pagesData = _h.pagesData, productionBaseline = _h.productionBaseline, activityMeta = _h.activityMeta, manipulativeBtnState = _h.manipulativeBtnState, playerBgPattern = _h.playerBgPattern, language = _h.language;
                    pagesData.forEach(function (page, index) {
                        actions.updatePageData({ pageIndex: index, data: page });
                    });
                    actions.setProductionBaseline(productionBaseline);
                    actions.setActivityMeta(activityMeta);
                    actions.setStoredManipulativeBtnState(manipulativeBtnState);
                    if (playerBgPattern)
                        actions.setPlayerBgPattern(playerBgPattern);
                    // The activity/share code may carry its own saved language (see
                    // WorkspaceData.language) that differs from this browser's persisted
                    // preference — content was already merged in that language above, so
                    // just sync store/i18n/document.lang/localStorage to match. Content
                    // is not re-merged here (setLanguage, not applyLanguage).
                    if (language !== currentLanguage)
                        actions.setLanguage(language);
                    actions.setPages({ currentPage: 1, totalPages: pagesData.length });
                    return [2 /*return*/, activityMeta];
                case 6:
                    err_1 = _v.sent();
                    console.error('loadActivityData error', err_1, source, getState());
                    message = err_1 instanceof Error ? err_1.message : String(err_1);
                    if (message.includes('ShareCodeDataSource: no data found')) {
                        window.location.href = '/';
                        return [2 /*return*/, undefined];
                    }
                    // JSON files not available — fall back to non-IDM/SWP mode
                    actions.setIsIDM(false);
                    actions.setIsSwp(false);
                    return [2 /*return*/, undefined];
                case 7: return [2 /*return*/];
            }
        });
    }); }),
};
export default playerStore;
